<script setup lang="ts">
import { mdiClose, mdiFileCheck, mdiImage, mdiInformation } from "@mdi/js";
import { useObjectUrl } from "@vueuse/core";
import type { DeepWritable } from "ts-essentials";
import type { components } from "~/api_types";
import { HEADER_TARGET, THUMB_TARGET } from "~/utils/imageCrop";

type ImageMetadata = components["schemas"]["ImageMetadata"];

interface Props {
  metadata: DeepWritable<ImageMetadata>;
  selectedFile: { base64: string; fileName: string } | null;
}
const modalOpen = defineModel<boolean>("open", {
  required: true,
});

interface Emits {
  (e: "confirm", metadata: ImageMetadata): void;
  (e: "cancel"): void;
  (e: "file-selected", file: { base64: string; fileName: string } | null): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n({ useScope: "local" });

// Create a local copy of metadata to avoid mutating props
const localMetadata = ref<ImageMetadata>({
  author: "",
  license: { text: "CC BY 4.0", url: "https://creativecommons.org/licenses/by/4.0/" },
});

// Drag and drop state
const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement>();
const dropZone = ref<HTMLElement>();
const fileError = ref<string>("");

// Watch for changes in props.metadata to update local copy
watch(
  () => props.metadata,
  (newMetadata: DeepWritable<ImageMetadata>) => {
    localMetadata.value = {
      author: newMetadata.author,
      license: { text: "CC BY 4.0", url: "https://creativecommons.org/licenses/by/4.0/" },
    };
  },
  { immediate: true }
);

// Crop selection. The thumb (256×256) and header (512×210) renders are centre-cropped in the data
// pipeline unless the submitter nudges these offsets - the same machinery the event form uses.
const thumbOffset = ref(0);
const headerOffset = ref(0);
const imageWidth = ref<number | null>(null);
const imageHeight = ref<number | null>(null);
// The cropper renders the picked image; decode it from the base64 the parent already holds so the
// modal stays self-contained whether opened fresh or reopened with a file still selected.
const previewSource = shallowRef<Blob | null>(null);
const previewUrl = useObjectUrl(previewSource);

watch(
  () => props.selectedFile,
  async (file) => {
    // A new (or cleared) image recentres both crops; their offset bounds depend on its dimensions.
    thumbOffset.value = 0;
    headerOffset.value = 0;
    if (!file) {
      imageWidth.value = null;
      imageHeight.value = null;
      previewSource.value = null;
      return;
    }
    const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes]);
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(blob);
    } catch {
      imageWidth.value = null;
      imageHeight.value = null;
      previewSource.value = null;
      return;
    }
    // A newer file may have arrived while decoding; drop this stale result if so.
    if (props.selectedFile !== file) {
      bitmap.close();
      return;
    }
    imageWidth.value = bitmap.width;
    imageHeight.value = bitmap.height;
    bitmap.close();
    previewSource.value = blob;
  },
  { immediate: true }
);

function confirmUpload(): void {
  emit("confirm", {
    ...localMetadata.value,
    // Mirrors `additionSchema.buildEvent`: omit offsets entirely when the crops stay centred.
    offsets:
      thumbOffset.value === 0 && headerOffset.value === 0
        ? null
        : { thumb: thumbOffset.value, header: headerOffset.value },
  });
}

// File upload handlers
function handleFileSelect(event: Event) {
  const file = (event.target as HTMLInputElement)?.files?.[0];
  if (file) {
    processFile(file);
  }
}

function handleDrop(event: DragEvent) {
  isDragOver.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0 && files[0]) {
    processFile(files[0]);
  }
}

function handleDragOver() {
  isDragOver.value = true;
}

function handleDragLeave(event: DragEvent) {
  // Only set to false if we're leaving the drop zone completely
  const rect = dropZone.value?.getBoundingClientRect();
  if (rect) {
    const x = event.clientX;
    const y = event.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      isDragOver.value = false;
    }
  }
}

function triggerFileInput() {
  fileInput.value?.click();
}

function removeSelectedFile() {
  emit("file-selected", null);
  // Reset the file input
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function processFile(file: File) {
  // Reset any previous errors
  fileError.value = "";

  // Validate file type
  const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
  if (!validTypes.includes(file.type)) {
    fileError.value = t("invalid_file_type");
    return;
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    fileError.value = t("file_too_large");
    return;
  }

  const fileName = file.name || "uploaded-file";
  const reader = new FileReader();
  reader.onload = (e) => {
    const result = e.target?.result as string;
    if (result) {
      const base64 = result.split(",")[1];
      if (base64) {
        emit("file-selected", { base64, fileName });
      }
    }
  };
  reader.onerror = () => {
    fileError.value = t("file_read_error");
  };
  reader.readAsDataURL(file);
}
</script>

<template>
  <Modal v-model="modalOpen" :title="t('image_metadata_title')" class="!min-w-[90vw]" @close="emit('cancel')">
    <div class="space-y-4">
      <!-- File Upload Drop Zone -->
      <div>
        <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-2"> {{ t("select_image") }} <span class="text-red-500 dark:text-red-400">*</span> </label>
        <div
          ref="dropZone"
          :class="[
            'border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 cursor-pointer relative overflow-hidden',
            isDragOver
              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900 scale-105 shadow-lg'
              : props.selectedFile
                ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800'
                : 'border-zinc-300 dark:border-zinc-600 bg-gradient-to-br from-zinc-50 dark:from-zinc-900 to-zinc-100 dark:to-zinc-800 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gradient-to-br hover:from-blue-50 dark:hover:from-blue-900 hover:to-zinc-100 dark:hover:to-zinc-800 hover:shadow-md',
          ]"
          @click="triggerFileInput"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <input ref="fileInput" type="file" accept="image/*" @change="handleFileSelect" class="hidden" />

          <div v-if="!props.selectedFile" class="space-y-3">
            <div :class="['transition-transform duration-300', isDragOver ? 'scale-110' : 'scale-100']">
              <MdiIcon :path="mdiImage" :size="64" class="mx-auto text-zinc-400 dark:text-zinc-500" />
            </div>
            <div :class="['transition-all duration-300', isDragOver ? 'text-blue-600 dark:text-blue-300' : 'text-zinc-600 dark:text-zinc-300']">
              <p class="text-lg font-medium">
                {{ isDragOver ? t("release_to_upload") : t("drop_image_here") }}
              </p>
              <p class="text-sm mt-1">{{ t("or_click_to_browse") }}</p>
              <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-2">{{ t("supported_formats", ["JPG, PNG, GIF, WebP (max 10MB)"]) }}</p>
            </div>
          </div>

          <div v-else class="space-y-3">
            <div>
              <MdiIcon :path="mdiFileCheck" :size="64" class="mx-auto text-blue-500 dark:text-blue-400" />
            </div>
            <div class="text-zinc-700 dark:text-zinc-200">
              <p class="text-lg font-medium text-blue-700 dark:text-blue-200">{{ props.selectedFile.fileName }}</p>
              <p class="text-sm text-blue-600 dark:text-blue-300 mb-3">{{ t("file_selected_successfully") }}</p>
              <button
                @click.stop="removeSelectedFile"
                class="inline-flex items-center px-3 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors duration-200"
              >
                <MdiIcon :path="mdiClose" :size="12" class="mr-1" />
                {{ t("remove_file") }}
              </button>
            </div>
          </div>
          <!-- Error message -->
          <p v-if="fileError" class="text-sm text-red-600 dark:text-red-300 mt-2 font-medium">{{ fileError }}</p>
        </div>
      </div>

      <!-- Crop selection -->
      <div v-if="previewUrl && imageWidth && imageHeight" class="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
        <div>
          <span class="block text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1">{{ t("crop_thumb") }}</span>
          <p class="text-zinc-500 dark:text-zinc-400 mb-1 text-xs">{{ t("crop_thumb_help") }}</p>
          <ImageCropper v-model="thumbOffset" :image-url="previewUrl" :width="imageWidth" :height="imageHeight" :target="THUMB_TARGET" />
        </div>
        <div>
          <span class="block text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1">{{ t("crop_header") }}</span>
          <p class="text-zinc-500 dark:text-zinc-400 mb-1 text-xs">{{ t("crop_header_help") }}</p>
          <ImageCropper v-model="headerOffset" :image-url="previewUrl" :width="imageWidth" :height="imageHeight" :target="HEADER_TARGET" />
        </div>
      </div>

      <!-- Author -->
      <div>
        <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1"> {{ t("image_author") }} <span class="text-red-500 dark:text-red-400">*</span> </label>
        <input
          v-model="localMetadata.author"
          type="text"
          :placeholder="t('image_author_placeholder')"
          class="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          required
        />
      </div>

      <!-- License Info -->
      <div>
        <div class="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div class="flex items-start">
            <MdiIcon :path="mdiInformation" :size="20" class="text-blue-500 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p class="text-sm font-medium text-blue-800 dark:text-blue-100">{{ t("license_info_title") }}</p>
              <p class="text-sm text-blue-700 dark:text-blue-200 mt-1">{{ t("license_info_description") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2 mt-6">
      <Btn variant="secondary" @click="emit('cancel')">
        {{ t("cancel") }}
      </Btn>
      <Btn variant="primary" @click="confirmUpload" :disabled="!localMetadata.author || !props.selectedFile">
        {{ t("confirm") }}
      </Btn>
    </div>
  </Modal>
</template>

<i18n lang="yaml">
de:
  image_metadata_title: Ein neues Bild vorschlagen
  select_image: Bild auswählen
  drop_image_here: Bild hier ablegen
  release_to_upload: Loslassen zum Hochladen
  or_click_to_browse: oder zum Durchsuchen klicken
  supported_formats: Unterstützt {0}
  file_selected_successfully: Datei erfolgreich ausgewählt
  remove_file: Datei entfernen
  invalid_file_type: Ungültiger Dateityp. Bitte wähle ein Bild (JPG, PNG, GIF, WebP).
  file_too_large: Datei zu groß (max 10MB).
  file_read_error: Fehler beim Lesen der Datei.
  selected_file: Ausgewählte Datei
  image_author: Autor
  image_author_placeholder: Wer hat dieses Bild erstellt?
  license_info_title: CC BY 4.0 - Frei zu verwenden mit Namensnennung
  license_info_description: Alle hochgeladenen Bilder werden unter der CC BY 4.0 Lizenz veröffentlicht. Das bedeutet, jeder kann das Bild verwenden, solange du als Autor genannt wirst.
  crop_thumb: Quadratischer Ausschnitt
  crop_thumb_help: Quadratischer Ausschnitt (256×256) für Vorschaubilder.
  crop_header: Header-Ausschnitt
  crop_header_help: Breiter Ausschnitt (512×210) für die Kopfzeile der Detailseite.
  confirm: Bild hinzufügen
  cancel: Abbrechen
en:
  image_metadata_title: Suggest a new Image
  select_image: Select Image
  drop_image_here: Drop image here
  release_to_upload: Release to upload
  or_click_to_browse: or click to browse
  supported_formats: Supports {0}
  file_selected_successfully: File selected successfully
  remove_file: Remove file
  invalid_file_type: Invalid file type. Please select an image (JPG, PNG, GIF, WebP).
  file_too_large: File too large (max 10MB).
  file_read_error: Error reading file.
  selected_file: Selected file
  image_author: Author
  image_author_placeholder: Who created this image?
  license_info_title: CC BY 4.0 - Free to use with attribution
  license_info_description: All uploaded images are published under the CC BY 4.0 license. This means anyone can use the image as long as they credit you as the author.
  crop_thumb: Square crop
  crop_thumb_help: Square crop (256×256) used for thumbnails.
  crop_header: Header crop
  crop_header_help: Wide crop (512×210) used for the detail page header.
  confirm: Add image
  cancel: Cancel
</i18n>
